import { FormattedMessage } from 'react-intl'
import { useMediaQuery, Theme } from '@mui/material'
import { AddOutlined } from '@mui/icons-material'
import { Button } from '@carto/meridian-ds/components'
import { useAppSelector } from 'app/state'
import BasicPage from 'features/common/ui/BasicPage/BasicPage'

import MapsFilter from './MapsFilter/MapsFilter'
import MapsGallery from './MapsGallery/MapsGallery'
import useUserRoles from 'features/account/state/useUserRoles'
import { useAccountQuotas } from 'features/quotas/ui/hooks'
import QuotaAlert from 'features/quotas/ui/QuotaAlert'
import MapsHardQuotaModal from './MapsHardQuotaModal'
import { useHardQuotaModalController } from 'features/quotas/ui/WouldExceedQuotaDialog'
import { useMemo } from 'react'
import { QuotaUxTrigger } from 'features/hubspot/types'
import useGetAccountData from 'features/account/ui/common/useGetAccountData'
import { BuilderPaths } from 'features/builder/paths'
import { openNewWindow } from 'features/common/utils/navigationUtils'
import { MapListFilters, MapOwnerFilter } from '../types'
import { useStateInUrl } from 'features/common/hooks/useStateInUrl'
import { DEFAULT_FILTERS } from 'features/maps/constants'
import { useMaps } from '../hooks/mapsHooks'
import MapsCounter from './MapsGallery/MapsCounter'
import MapsEmpty from './MapsGallery/MapsEmpty'
import MapsSample from './MapsSample/MapsSample'

function MapsPage() {
  const { userHasEditionRole } = useUserRoles()
  const { usageBasedPricing } = useGetAccountData()
  const stats = useAppSelector((state) => state.account.statsData.stats)

  const [tagsFilter, setTagsFilter] = useStateInUrl('tags', DEFAULT_FILTERS.tags, Array)
  const [permissionsFilter, setPermissionsFilter] = useStateInUrl('permissions', DEFAULT_FILTERS.permissions)
  const [searchFilter, setSearchFilter] = useStateInUrl('search', DEFAULT_FILTERS.search)
  const [order, setOrder] = useStateInUrl('order', DEFAULT_FILTERS.order, Object)

  const filters: MapListFilters = useMemo(
    () => ({
      tags: tagsFilter,
      permissions: permissionsFilter,
      search: searchFilter,
      order
    }),
    [tagsFilter, permissionsFilter, searchFilter, order]
  )

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useMaps(filters)

  const total = data?.pages?.[0]?.meta?.page?.total || 0
  const hasMaps = (stats?.totalMaps || 0) > 0
  const hasActiveFilters = Boolean(
    searchFilter || permissionsFilter !== MapOwnerFilter.All || (tagsFilter && tagsFilter.length > 0)
  )
  const showMaps = total > 0 || hasActiveFilters

  const handleFilterChange = (draft: Partial<MapListFilters>) => {
    if (Object.prototype.hasOwnProperty.call(draft, 'tags')) {
      setTagsFilter(draft.tags)
    } else if (Object.prototype.hasOwnProperty.call(draft, 'permissions')) {
      setPermissionsFilter(draft.permissions)
    } else if (Object.prototype.hasOwnProperty.call(draft, 'search')) {
      setSearchFilter(draft.search)
    } else if (Object.prototype.hasOwnProperty.call(draft, 'order')) {
      setOrder(draft.order)
    }
  }

  const error = useAppSelector((state) => state.maps.myMapsError)
  const isXs = useMediaQuery((theme: Theme) => theme.breakpoints.only('xs'))

  const { mapsQuota } = useAccountQuotas()
  const hardQuotaModalController = useHardQuotaModalController(mapsQuota)

  const createMap = () => {
    if (hardQuotaModalController.additionWouldExceedQuota()) {
      hardQuotaModalController.open()
      return
    }
    openNewWindow(BuilderPaths.NewEmptyMap)
  }

  const shouldDisplayNewMapButton = userHasEditionRole && hasMaps // if user has no maps, there is a special CTA

  const quotaAlert = useMemo(() => {
    return (
      !usageBasedPricing &&
      userHasEditionRole &&
      mapsQuota &&
      mapsQuota?.hit && (
        <QuotaAlert
          message={<FormattedMessage id="maps.quota.limitHit" values={{ limit: mapsQuota.limit }} />}
          moreInfoModal={(props) => (
            <MapsHardQuotaModal
              {...props}
              quota={mapsQuota}
              variant="moreInfo"
              quotaUxTrigger={QuotaUxTrigger.MoreInfo}
            />
          )}
        />
      )
    )
  }, [mapsQuota, userHasEditionRole, usageBasedPricing])

  return (
    <BasicPage
      titleId="maps.title"
      hasError={error}
      afterTitleContent={
        showMaps || hasActiveFilters ? (
          <MapsFilter total={total} filters={filters} onChange={handleFilterChange} />
        ) : null
      }
      primaryAction={
        shouldDisplayNewMapButton && (
          <Button onClick={createMap} variant="contained" color="primary" startIcon={<AddOutlined />}>
            <FormattedMessage id={`maps.${isXs ? 'titleButtonShort' : 'titleButton'}`} />
          </Button>
        )
      }
      showScrollToTop={hasMaps}
      beforeTitleContent={quotaAlert}
    >
      {showMaps || isLoading ? (
        <>
          {!!total && !filters.search && <MapsCounter mapsCount={total} />}
          <MapsGallery
            isLoading={isLoading && !isFetchingNextPage}
            data={data}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            hasActiveFilters={hasActiveFilters}
          />
        </>
      ) : (
        <>
          <MapsEmpty />
          {userHasEditionRole && <MapsSample />}
        </>
      )}
      <MapsHardQuotaModal variant="create" {...hardQuotaModalController.props} quotaUxTrigger={QuotaUxTrigger.NewMap} />
    </BasicPage>
  )
}
export default MapsPage
